import React from 'react';
import { Box, Tooltip, IconButton, useMediaQuery } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, HelpOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

const logoData = [
  {
    type: 'img',
    src: require('../../../assets/logos/tech/metatrader-4.png'),
    alt: 'MetaTrader 4',
    tooltip: 'MetaTrader 4',
  },
  {
    type: 'img',
    src: require('../../../assets/logos/tech/metatrader-5.png'),
    alt: 'MetaTrader 5',
    tooltip: 'MetaTrader 5',
  },
  {
    type: 'img',
    src: require('../../../assets/logos/tech/cqg.png'),
    alt: 'CQG',
    tooltip: 'CQG',
  },
  {
    type: 'img',
    src: require('../../../assets/logos/tech/finalto.jpg'),
    alt: 'Finalto',
    tooltip: 'Finalto',
  },
  {
    type: 'img',
    src: require('../../../assets/logos/tech/integral.png'),
    alt: 'Integral',
    tooltip: 'Integral',
  },
  {
    type: 'img',
    src: require('../../../assets/logos/tech/interactive-brookers.png'),
    alt: 'Interactive Brokers',
    tooltip: 'Interactive Brokers',
  },
];

const AboutLogosRow = () => {
  const theme = useTheme();
  const scrollRef = React.useRef(null);
  const [showLeftControl, setShowLeftControl] = React.useState(false);
  const [showRightControl, setShowRightControl] = React.useState(true);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Smooth scroll by one logo width
  const scrollBy = (dir) => {
    if (isMobile) {
      setCurrentIndex((prev) => (prev + dir + logoData.length) % logoData.length);
      return;
    }
    if (scrollRef.current) {
      const logoWidth = 88 + 16; // width + gap (px)
      scrollRef.current.scrollBy({ left: dir * logoWidth, behavior: 'smooth' });
    }
  };

  // Check if controls should be visible
  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftControl(scrollLeft > 0);
      setShowRightControl(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  React.useEffect(() => {
    if (isMobile) return undefined;
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      // Initial check
      setTimeout(handleScroll, 100);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [isMobile]);

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.4,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 5, minHeight: { xs: 160, sm: 180 } }}>
      {/* Left Control - Always show on mobile, conditional on desktop */}
      <IconButton 
        aria-label="Scroll left" 
        onClick={() => scrollBy(-1)} 
        sx={{ 
          position: 'absolute', 
          left: { xs: 8, md: -16 }, 
          zIndex: 2,
          display: { xs: (isMobile && logoData.length > 2) ? 'flex' : 'none', md: showLeftControl ? 'flex' : 'none' },
          color: 'primary.main',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          }
        }}
      >
        <ArrowBackIos fontSize="small" />
      </IconButton>

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: { xs: 2, sm: 3 },
          overflowX: { xs: 'auto', md: 'visible' },
          overflowY: 'visible',
          justifyContent: 'center',
          alignItems: 'center',
          scrollBehavior: 'smooth',
          maxWidth: { xs: 'calc(100% - 96px)', md: '100%' },
          px: { xs: 0, md: 8 },
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {logoData.map((logo, idx) => {
          const isVisibleMobile = isMobile
            ? idx === currentIndex || idx === (currentIndex + 1) % logoData.length
            : true;
          return (
          <Box
            key={idx}
            sx={{
              display: { xs: isVisibleMobile ? 'block' : 'none', md: 'block' },
            }}
          >
            <motion.div
              custom={idx}
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <Tooltip title={logo.tooltip} arrow>
                {logo.type === 'img' ? (
                  <motion.img
                    src={logo.src}
                    alt={logo.alt}
                    draggable={false}
                    whileHover={{ scale: 1.08, y: -4 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    style={{
                      width: 'clamp(96px, 10vw, 110px)',
                      height: 'clamp(96px, 10vw, 110px)',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, rgba(109,0,119,0.08) 0%, rgba(4,203,255,0.06) 100%)',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.25s',
                      scrollSnapAlign: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.08)';
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 'clamp(72px, 8vw, 96px)',
                      height: 'clamp(72px, 8vw, 96px)',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(131,9,254,0.08) 0%, rgba(4,203,255,0.08) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                      transition: 'box-shadow 0.25s, transform 0.25s',
                      scrollSnapAlign: 'center',
                      fontSize: { xs: 36, sm: 48 },
                      color: theme.palette.grey[700],
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
                        transform: 'translateY(-3px) scale(1.08)',
                      },
                    }}
                  >
                    <HelpOutline fontSize="inherit" />
                  </Box>
                )}
              </Tooltip>
            </motion.div>
          </Box>
          );
        })}
      </Box>

      {/* Right Control - Always show on mobile, conditional on desktop */}
      <IconButton 
        aria-label="Scroll right" 
        onClick={() => scrollBy(1)} 
        sx={{ 
          position: 'absolute', 
          right: { xs: 8, md: -16 }, 
          zIndex: 2,
          display: { xs: (isMobile && logoData.length > 2) ? 'flex' : 'none', md: showRightControl ? 'flex' : 'none' },
          color: 'primary.main',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          }
        }}
      >
        <ArrowForwardIos fontSize="small" />
      </IconButton>

      {/* Edge fades for smooth visual clipping - mobile only */}
      <Box sx={{ position: 'absolute', left: { xs: 48, md: 0 }, top: 0, bottom: 0, width: 32, background: 'linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,0))', pointerEvents: 'none', display: { xs: 'block', md: 'none' } }} />
      <Box sx={{ position: 'absolute', right: { xs: 48, md: 0 }, top: 0, bottom: 0, width: 32, background: 'linear-gradient(270deg, rgba(255,255,255,1), rgba(255,255,255,0))', pointerEvents: 'none', display: { xs: 'block', md: 'none' } }} />
    </Box>
  );
};

export default AboutLogosRow;
